/**
 * 老师口型片段的查表与解析
 * ============================================================
 * 预生成的口型视频按**句子文本**存成一个文件（见 clipHash.js 的命名理由）。
 * 本模块负责把「当前要说的这句话」解析成「该播哪个片段」。
 *
 * ── 与 teacherAssets.js 的分工 ───────────────────────────────
 *   teacherAssets.js   老师级素材：立绘、形象来源、口型能力标注
 *   本模块              句子级素材：某一句有没有对应的口型片段
 *   两者都读同一个 manifest，但职责不同，不合并 ——
 *   立绘是「一个人一张」，片段是「一句话一段」，生命周期与更新方式都不一样。
 *
 * ── 为什么查表要同步 ─────────────────────────────────────────
 *   调用点在播放路径上（点一下就要出声）。如果每次播放都 await 一次
 *   manifest 请求，用户会感知到明显延迟，且离线时无法降级。
 *   所以：manifest 异步加载一次 → 缓存成 Set → 之后查表是纯同步 O(1)。
 *
 * ── 为什么「清单没命中」也要给出 URL（重要）──────────────────
 *   片段是**逐步生成**的，而且客户端**可能拿着过期的清单**：
 *   manifest 是会被反复更新的资源，一旦浏览器缓存住旧版本（或某个
 *   会话一直没重新拉），hashes 就会长期停在旧的那一批。
 *
 *   历史事故：早期这版用 `fetch(manifest, {cache:'force-cache'})`，
 *   清单被永久缓存，新增的 15 条「开场讲解」片段**永远查不到**。
 *   症状极具迷惑性 —— 讲解（普通话）全程不动，而课程句子（粤语）
 *   正常，看起来就像「普通话那套功能没实现」。服务端、文件、
 *   哈希全都是对的，只有客户端在用过期的表。
 *
 *   所以现在的策略是**乐观尝试**：
 *     · 清单命中        → 直接给 URL（快路径，零额外请求）
 *     · 清单没命中      → **仍然给 URL**，让 <video> 用 onError 说话
 *     · 确实不存在      → 记入负缓存，同一句不再重复发 404
 *   代价是每句最多一次 404；换来的是「清单旧了也能照常播」，
 *   不会再出现整批片段静默失效的情况。
 *
 *   ⚠️ 之所以敢乐观试，是因为播放层有 poster（立绘）兜底：
 *      视频加载失败不会黑屏，只会安静回退到 TTS，用户几乎无感。
 */

import { clipHash } from './clipHash';
import { assetUrl } from './assetUrl';
import { loadTeacherManifest } from './teacherAssets';

/** tutorId → { dir, hashes:Set, count, optimistic } */
const clipsCache = new Map();

/**
 * 按方言解析该用哪位老师（形象 + 口型）
 *
 * ── 为什么老师要跟着方言走 ──────────────────────────────────
 * 官方 TTS 音色**有性别，且部分方言只有一种性别没有可选余地**：
 *   女声：粤语 `longanfengyue`、四川 `Sunny`、上海 `Jada`      → 阿清
 *   男声：陕西 `Marcus`、南京 `Li`、闽南 `Roy`               → 阿峰
 * 所以「哪个方言用哪位老师」不是偏好问题，是**音频决定的**。
 * 让学生选老师、然后听到与形象性别不符的声音，只会以为配音坏了。
 *
 * 映射表放在 manifest 的 `dialectTutors` 里（数据而非代码），
 * 换老师、换立绘都不需要动前端。
 *
 * @param {string} dialectId
 * @param {string} [fallbackId='aqing'] 映射表缺失时的兜底
 * @returns {Promise<string>} tutorId
 */
export async function resolveTutorForDialect(dialectId, fallbackId = 'aqing') {
  try {
    const manifest = await loadTeacherManifest();
    const dt = manifest?.dialectTutors;
    if (dialectId && dt?.map?.[dialectId]) return dt.map[dialectId];
    return dt?.default || fallbackId;
  } catch {
    // 清单拿不到时不阻断上课：沿用调用方的兜底，素材层另有占位逻辑
    return fallbackId;
  }
}

/**
 * 已确认不存在的片段哈希（负缓存）
 *
 * 乐观尝试必然带来 404，这里把失败过的记下来，
 * 同一句话在本次会话内不再重复请求 —— 否则每播一次就发一个 404，
 * 网络面板会很脏，弱网下还会拖慢回退速度。
 */
const missingClips = new Set();

/** 空结果：素材未配置或加载失败时统一用它，避免各处判空 */
const EMPTY = Object.freeze({
  dir: null,
  hashes: new Set(),
  count: 0,
  optimistic: false,
});

/**
 * 读取某位老师**某个方言**的片段清单
 *
 * manifest 有两种形态：
 *   · 顶层 dir/hashes              —— 粤语（主目录，历史结构，保持兼容）
 *   · clips.dialects.<dialectId>   —— 其它方言各自的子目录
 *
 * ⚠️ 当前方言没有片段时**必须返回空**，绝不能退回粤语目录：
 *    粤语的口型是按粤语音频长出来的，配上方言语音会音画全错，
 *    比「没有口型」糟糕得多（用户会以为老师读错了）。
 *
 * @param {string} tutorId
 * @param {string} [dialectId='yue-HK'] 当前方言；决定去哪个子目录取片段
 * @returns {Promise<{dir:string|null, hashes:Set<string>, count:number, optimistic:boolean}>}
 */
export async function loadTutorClips(tutorId, dialectId = 'yue-HK') {
  if (!tutorId) return EMPTY;

  const cacheKey = `${tutorId}::${dialectId}`;
  if (clipsCache.has(cacheKey)) return clipsCache.get(cacheKey);

  let info = EMPTY;
  try {
    const manifest = await loadTeacherManifest();
    const tutor = manifest?.tutors?.[tutorId];
    const clips = tutor?.clips;

    /*
     * 判定「这位老师有没有启用片段机制」。
     *
     * 看两处：
     *   · lipSync === 'video-clip'  —— 能力声明（manifest 里明确写了）
     *   · 有清单                    —— 实际有货
     * 任一成立就视为启用。都拿不到才回退 EMPTY（走纯 TTS + 立绘）。
     *
     * 注意：**不能要求清单非空才启用** —— 那又回到「清单旧了就全废」。
     * 只要能力声明在，就给一个 dir 让调用方去乐观试。
     */
    const declared = tutor?.lipSync === 'video-clip';
    const entry = clips?.dialects?.[dialectId];
    /**
     * 顶层 dir/hashes 属于哪个方言 —— **必须由 manifest 显式声明**。
     *
     * 曾经这里写的是 `else if (dialectId === 'yue-HK' || !clips?.dialects)`，
     * 意思是"清单里没有 dialects 字段 ⇒ 只有粤语 ⇒ 就当成粤语"。
     * 这个推断是**危险**的：一旦 manifest 里的 dialects 被清空（比如某个方言
     * 的片段全部下线），所有非粤语方言都会满足 `!clips.dialects`
     * 而**落到粤语目录**，正好触发本节开头警告的"音画全错"。
     * 事后必须显式声明，不靠"字段缺省"猜。
     *
     * 取值三态：
     *   未声明（undefined） → 按历史数据当粤语（向后兼容）
     *   显式给方言 id       → 顶层就是那个方言（阿清 = yue-HK）
     *   显式 null           → **没有主方言**（阿峰：他的片段全在 dialects 下）
     */
    const rootDialect = clips?.rootDialect === undefined ? 'yue-HK' : clips.rootDialect;

    let dir = null;
    let hashes = [];
    let count = 0;

    if (entry) {
      // 方言专属目录
      dir = entry.dir || `/teachers/${tutorId}/clips/${dialectId}`;
      hashes = Array.isArray(entry.hashes) ? entry.hashes : [];
      count = entry.count || hashes.length;
    } else if (rootDialect && dialectId === rootDialect) {
      // 顶层目录就是 rootDialect 的片段
      dir = clips?.dir || `/teachers/${tutorId}/clips`;
      hashes = Array.isArray(clips?.hashes) ? clips.hashes : [];
      count = clips?.count || hashes.length;
    }
    // 其它情况：该方言没配片段 → dir 保持 null → EMPTY（明确不播视频）

    /*
     * 补上站点 base。
     *
     * manifest 里存的是 `/teachers/...`（站点内绝对路径）。部署在根目录时
     * 这样写没问题；放到子目录（如 /dialectgo/）就必须补前缀，
     * 否则片段请求指向域名根，404 且**不报错** —— 视频元素只会说
     * "格式不支持"，与真正原因差得很远。
     */
    if (dir) dir = assetUrl(dir);

    if (dir && (declared || hashes.length)) {
      info = {
        dir,
        hashes: new Set(hashes),
        count,
        // 声明了能力就可以乐观试：清单可能过期，宁可多一次 404 也别漏播
        optimistic: true,
      };
    }
  } catch (e) {
    // 片段加载失败**不能影响上课** —— 回退到 TTS + 立绘即可
    console.warn('[tutorClip] 片段清单加载失败，回退立绘：', e?.message);
  }

  clipsCache.set(cacheKey, info);
  return info;
}

/**
 * 取某句话的片段 URL
 *
 * @param {string} text       - 句子原文（必须与生成端完全一致）
 * @param {Object} clipsInfo  - loadTutorClips 的返回值
 * @returns {string|null}     确定不可能有片段时返回 null，调用方据此走 TTS 路径
 */
export function resolveClipUrl(text, clipsInfo) {
  if (!clipsInfo || !clipsInfo.dir) return null;

  const hash = clipHash(text);
  if (!hash) return null;

  // 之前 404 过的句子：直接判无，不再重试
  if (missingClips.has(hash)) return null;

  const known = clipsInfo.hashes.has(hash);
  // 清单里没有、又不允许乐观尝试 → 只有这种才是真的没有
  if (!known && !clipsInfo.optimistic) return null;

  return `${String(clipsInfo.dir).replace(/\/+$/, '')}/${hash}.mp4`;
}

/**
 * 某句话**确定**有片段（清单命中）
 *
 * 与 resolveClipUrl 的区别：这个只回答「清单里明确有吗」，
 * 不乐观。用在不需要真的拼 URL、只想提前分流的地方。
 * 想判断「这次会不会播视频」请用 resolveClipUrl 的结果。
 *
 * @param {string} text
 * @param {Object} clipsInfo
 * @returns {boolean}
 */
export function hasClip(text, clipsInfo) {
  if (!clipsInfo || !clipsInfo.dir) return false;
  const hash = clipHash(text);
  return !!hash && clipsInfo.hashes.has(hash) && !missingClips.has(hash);
}

/**
 * 标记某句话的片段**不存在**（播放层 onError 时调用）
 *
 * 乐观尝试的配套：视频 404 / 解码失败后把哈希记下来，
 * 避免同一句反复发请求。
 *
 * @param {string} text - 句子原文（优先用文本，调用方不必自己算哈希）
 */
export function markClipMissing(text) {
  const hash = clipHash(text);
  if (hash) missingClips.add(hash);
}

/**
 * 从片段 URL 反查哈希并记为不存在
 *
 * TeacherPortraitStage 的 onError 只拿得到 URL，用这个更省事。
 * URL 形如 `/teachers/aqing/clips/<hash>.mp4`。
 *
 * @param {string} url
 * @returns {string|null} 提取到的哈希
 */
export function markClipMissingByUrl(url) {
  const m = /([0-9a-f]{16})\.mp4(?:[?#].*)?$/i.exec(String(url || ''));
  if (!m) return null;
  missingClips.add(m[1]);
  return m[1];
}

/** 清缓存（素材更新后热更新、测试用） */
export function _clearTutorClipsCache() {
  clipsCache.clear();
  missingClips.clear();
}

/** 同步注入（测试用：跳过网络，直接给一份清单） */
export function _seedTutorClips(tutorId, hashes, dir, optimistic, dialectId = 'yue-HK') {
  clipsCache.set(`${tutorId}::${dialectId}`, {
    dir: assetUrl(dir || `/teachers/${tutorId}/clips`),
    hashes: new Set(hashes || []),
    count: (hashes || []).length,
    optimistic: optimistic !== false,
  });
}
